import _extends from "@babel/runtime/helpers/extends";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["autoFocus", "className", "dangerouslyForceActiveStyling", "dangerouslyForceFocusStyling", "dangerouslyForceHoverStyling", "dangerouslyForceSelectedStyling", "disabled", "name", "onChange", "options", "value"];
import "core-js/modules/es6.function.name.js";
import "core-js/modules/es6.array.map.js";
import "core-js/modules/es6.object.to-string.js";
import "core-js/modules/es6.regexp.to-string.js";
import React from 'react';
import classnames from 'classnames';
import { useId } from '@nerdwallet/react-accessibility';
import { useDeprecatedClassNameStylePropsWarning } from '../../internal/deprecated-classname-style-props';
import style from './CardSelect.module.less';

/**
 * `CardSelect` components present a list of options for a user to choose from. The option selected
 * influences or changes the experience. Users can select a single option from the list.
 *
 * This iteration deprecates the multiple prop until usability issues are resolved. Please reach out
 * for more details.
 */
export var CardSelect = /*#__PURE__*/React.forwardRef(function (props, ref) {
  var autoFocus = props.autoFocus,
      className = props.className,
      dangerouslyForceActiveStyling = props.dangerouslyForceActiveStyling,
      dangerouslyForceFocusStyling = props.dangerouslyForceFocusStyling,
      dangerouslyForceHoverStyling = props.dangerouslyForceHoverStyling,
      dangerouslyForceSelectedStyling = props.dangerouslyForceSelectedStyling,
      disabled = props.disabled,
      nameProp = props.name,
      onChange = props.onChange,
      options = props.options,
      selectedValue = props.value,
      otherProps = _objectWithoutProperties(props, _excluded);

  useDeprecatedClassNameStylePropsWarning('CardSelect', {
    className: className
  });

  var createChangeHandler = function createChangeHandler(value) {
    return function () {
      onChange === null || onChange === void 0 ? void 0 : onChange(value);
    };
  }; // `name` is used to link related radio buttons together for keyboard
  // navigation. If the name isn't provided, we can use `useId()` to generate
  // one, since it doesn't really matter what the name is as long as it's a
  // globally-unique string.


  var name = useId(nameProp); // disabling this rule because we do use nesting by putting the input inside of the label
  // this is valid according to this page https://github.com/evcohen/eslint-plugin-jsx-a11y/blob/master/docs/rules/label-has-for.md

  return /*#__PURE__*/React.createElement("div", _extends({}, otherProps, {
    className: classnames(style.container, className),
    ref: ref
  }), options.map(function (_ref, index) {
    var _classnames;

    var label = _ref.label,
        value = _ref.value,
        optionDisabled = _ref.disabled,
        _ref$onClick = _ref.onClick,
        onClick = _ref$onClick === void 0 ? function () {} : _ref$onClick;
    var isChecked = value === selectedValue; // only pass this prop in if props.autoFocus is true

    var autoFocusProp = // use autoFocus on the first selected card, and if none are selected, the first card
    autoFocus && (selectedValue ? isChecked : index === 0);
    var isDisabled = disabled || optionDisabled;
    return /*#__PURE__*/React.createElement("div", {
      className: classnames(style.option, className),
      key: value.toString()
    }, /*#__PURE__*/React.createElement("label", {
      className: classnames(style.label, (_classnames = {}, _defineProperty(_classnames, style.labelSelected, isChecked), _defineProperty(_classnames, style.disabled, isDisabled), _defineProperty(_classnames, style['force-active'], dangerouslyForceActiveStyling), _defineProperty(_classnames, style['force-focus'], dangerouslyForceFocusStyling), _defineProperty(_classnames, style['force-hover'], dangerouslyForceHoverStyling), _defineProperty(_classnames, style['force-selected'], dangerouslyForceSelectedStyling), _classnames))
    }, label, /*#__PURE__*/React.createElement("input", {
      // eslint-disable-next-line jsx-a11y/no-autofocus
      autoFocus: autoFocusProp,
      onClick: onClick,
      type: "radio",
      className: style.input,
      onChange: createChangeHandler(value),
      checked: isChecked,
      disabled: isDisabled,
      name: name
    })));
  }));
});